// Copyright 2025 Sorama B.V.
import { autoinject, bindable, computedFrom } from "aurelia-framework";
import { CamIV64DeviceType } from "services/Hardware/CamHTTP/CamIV64Helpers";

export interface PricingPackage {
    id: string;
    name: string;
    description: string;
    features: string[];
    buttonText: string;
    buttonDisabled: boolean;
    shopify: ShopifyVariables;
}

export interface SubPackage {
    id: string;
    name: string;
    icon: string;
    description: string;
    features: string[];
    buttonText: string;
    buttonDisabled: boolean;
    shopify: ShopifyVariables;
    isMobilePanelOpen: boolean;
}
export type ShopifyVariables = {
    productId: string;
    variantId: {
        annually: {
            iV64s?: string;
            iV64Ex?: string;
            iV64?: string;
        };
        lifetime: {
            iV64s?: string;
            iV64Ex?: string;
            iV64?: string;
        };
    };
};

@autoinject
export class PricingSection {
    @bindable
    public isLifetime: boolean;

    @bindable
    public deviceType: CamIV64DeviceType;

    @computedFrom("deviceType")
    public get packages(): PricingPackage[] {
        if (this.deviceType === "iV64") {
            return this.iV64Packages;
        }
        return this.industrialPackages;
    }

    @computedFrom("deviceType")
    public get subPackages(): SubPackage[] {
        if (this.deviceType === "iV64") {
            return this.iV64SubPackages;
        }
        return this.industrialSubPackages;
    }

    private industrialSubPackages: SubPackage[] = [
        {
            id: "leak-inspection",
            icon: "/resources/images/icons/pipe.svg",
            name: "Leak inspection",
            description: "Detect and locate gas and air leaks with precision.",
            features: [
                "Advanced Package",
                "Multi-leak visualization",
                "Multi-leak rate",
                "Leak rate conversion",
                "On device cost refinement",
            ],
            buttonText: "Buy now",
            buttonDisabled: false,
            isMobilePanelOpen: false,
            shopify: {
                productId: "10287944565069",
                variantId: {
                    annually: {
                        iV64s: "52469476196685",
                        iV64Ex: "52469476262221",
                    },
                    lifetime: {
                        iV64s: "52469476229453",
                        iV64Ex: "52469476294989",
                    },
                },
            },
        },
        {
            id: "partial-discharge",
            icon: "/resources/images/icons/bolt.svg",
            name: "Partial discharge inspection",
            description: "Identify and analyze electrical discharges early",
            features: ["Advanced Package", "Real-time PD detection", "Pattern recognition", "Severity assessment"],
            buttonText: "Coming soon",
            buttonDisabled: true,
            isMobilePanelOpen: false,
            shopify: {
                productId: "10287945384269",
                variantId: {
                    annually: {
                        iV64s: "52469477409101",
                        iV64Ex: "52469477474637",
                    },
                    lifetime: {
                        iV64s: "52469477441869",
                        iV64Ex: "52469477507405",
                    },
                },
            },
        },
        {
            id: "mechanical",
            icon: "/resources/images/icons/gears.svg",
            name: "Mechanical inspection",
            description: "Monitor and diagnose mechanical issues quickly.",
            features: ["Advanced Package", "Vibration analysis", "Bearing fault detection", "Motor health monitoring"],
            buttonText: "Buy now",
            buttonDisabled: false,
            isMobilePanelOpen: false,
            shopify: {
                productId: "10287946137933",
                variantId: {
                    annually: {
                        iV64s: "52469478687053",
                        iV64Ex: "52469478752589",
                    },
                    lifetime: {
                        iV64s: "52469478719821",
                        iV64Ex: "52469478785357",
                    },
                },
            },
        },
        {
            id: "ultimate",
            icon: "/resources/images/icons/trophy.svg",
            name: "Ultimate",
            description: "The complete solution with all features.",
            features: [
                "Advanced Package",
                "All inspection types",
                "Advanced analytics",
                "Priority support",
                "Custom reporting",
            ],
            buttonText: "Buy now",
            buttonDisabled: false,
            isMobilePanelOpen: false,
            shopify: {
                productId: "10287949349197",
                variantId: {
                    annually: {
                        iV64s: "52469482455373",
                        iV64Ex: "52469482520909",
                    },
                    lifetime: {
                        iV64s: "52469482488141",
                        iV64Ex: "52469482553677",
                    },
                },
            },
        },
    ];

    private industrialPackages: PricingPackage[] = [
        {
            id: "advanced",
            name: "ADVANCED",
            description:
                "Perfect for essential sound analysis. Includes the fundamental features for basic leak, partial discharge, and mechanical inspections.",
            features: [
                "Extended frequency selection",
                "High Sample Rate (240 kHz)",
                "Unlimited video recording",
                "500GB of internal storage",
                "Email reporting",
                "Basic leak, partial discharge and mechanical inspection",
            ],
            buttonText: "Buy now",
            buttonDisabled: false,
            shopify: {
                productId: "10287917105485",
                variantId: {
                    annually: {
                        iV64s: "52469427568973",
                        iV64Ex: "52469475443021",
                    },
                    lifetime: {
                        iV64s: "52469427601741",
                        iV64Ex: "52469475475789",
                    },
                },
            },
        },
        {
            id: "expert",
            name: "EXPERT *",
            description:
                "The Expert package includes all features from the Advanced package, plus additional tools for Leak Inspection, Partial Discharge Inspection, and Mechanical Inspection.",
            features: [],
            buttonText: "Available as sub-packages",
            buttonDisabled: true,
            shopify: {
                productId: "",
                variantId: {
                    annually: {
                        iV64s: "",
                        iV64Ex: "",
                    },
                    lifetime: {
                        iV64s: "",
                        iV64Ex: "",
                    },
                },
            },
        },
    ];

    private iV64Packages: PricingPackage[] = [
        {
            id: "advanced",
            name: "ADVANCED",
            description:
                "Perfect for comprehensive sound analysis and everyday inspections. Includes key tools for detecting and documenting acoustic events.",
            features: [
                "Extended frequency selection",
                "30s of video recording",
                "500GB of internal storage",
                "Basic SLM and spectrogram",
                "Adjustable blob size and zoom",
                "Sound markers and photo notes",
            ],
            buttonText: "Buy now",
            buttonDisabled: false,
            shopify: {
                productId: "10287917105485",
                variantId: {
                    annually: {
                        iV64: "53863730676045",
                    },
                    lifetime: {
                        iV64: "53863730708813",
                    },
                },
            },
        },
        {
            id: "expert",
            name: "EXPERT",
            description:
                "The Expert package includes all features from the Advanced package, with additional tools for in-depth analysis and inspection workflows. Designed for professionals who require deeper insights and enhanced reporting capabilities.",
            features: [],
            buttonText: "Available as sub-packages",
            buttonDisabled: true,
            shopify: {
                productId: "",
                variantId: {
                    annually: {},
                    lifetime: {},
                },
            },
        },
        {
            id: "ultimateSuite",
            name: "ULTIMATE SUITE",
            description:
                "The complete solution for advanced acoustic diagnostics. Combines all features into one powerful package.",
            features: ["Includes all features from the Advanced & Expert packages"],
            buttonText: "Coming Soon",
            buttonDisabled: true,
            shopify: {
                productId: "",
                variantId: {
                    annually: {},
                    lifetime: {},
                },
            },
        },
    ];

    private iV64SubPackages: SubPackage[] = [
        {
            id: "interior",
            icon: "",
            name: "Interior",
            description: "Assess sound behavior in enclosed environments.",
            features: [
                "Advanced SLM",
                "RT60 analysis",
                "Speech Level Reduction",
                "Sound Reduction Index",
                "NTi integration",
                " Email reporting",
            ],
            buttonText: "Coming soon",
            buttonDisabled: true,
            isMobilePanelOpen: false,
            shopify: {
                productId: "",
                variantId: {
                    annually: {},
                    lifetime: {},
                },
            },
        },
        {
            id: "exterior",
            icon: "",
            name: "Exterior",
            description: "Identify acoustic anomalies with leak scan.",
            features: ["Leak scan", "Leak scan indication"],
            buttonText: "Coming soon",
            buttonDisabled: true,
            isMobilePanelOpen: false,
            shopify: {
                productId: "",
                variantId: {
                    annually: {},
                    lifetime: {},
                },
            },
        },
    ];

    private shopifyStyle = {
        button: {
            "font-size": "14px",
            "font-weight": "400",
            padding: "0.7rem 2rem",
            "border-radius": "3px",
            transition: "all 0.2s ease",
            "background-color": "#a1b2c4",
            color: "#2d3e50",
        },
        price: {
            "font-size": "24px",
            "font-weight": "700",
            color: "#fbfbfb",
        },
        buttonWrapper: {
            "margin-top": "0px",
            "margin-bottom": "1rem",
        },
    };
    private toggleCollapse(pkg: SubPackage) {
        pkg.isMobilePanelOpen = !pkg.isMobilePanelOpen;
    }

    public getVariantId(pkg: PricingPackage | SubPackage, isLifetime: boolean): string {
        if (this.deviceType === "iV64") {
            return isLifetime ? pkg.shopify.variantId.lifetime.iV64 : pkg.shopify.variantId.annually.iV64;
        } else if (this.deviceType === "iV64Ex") {
            return isLifetime ? pkg.shopify.variantId.lifetime.iV64Ex : pkg.shopify.variantId.annually.iV64Ex;
        } else {
            return isLifetime ? pkg.shopify.variantId.lifetime.iV64s : pkg.shopify.variantId.annually.iV64s;
        }
    }

    public buyPackage(packageId: string): void {
        if (packageId === "advanced" || packageId === "leak-inspection") {
            console.log(`Buying package: ${packageId}`);
        }
    }

    public get advancedPackage(): PricingPackage {
        return this.packages.find((p) => p.id === "advanced") ?? this.packages[0];
    }

    public get expertPackage(): PricingPackage {
        return this.packages.find((p) => p.id === "expert") ?? this.packages[1];
    }
    public get ultimateSuitePackage(): PricingPackage {
        return this.packages.find((p) => p.id === "ultimateSuite") ?? this.packages[2];
    }
}
